/**
 * China Payment (MYP-10): the separate RMB service — topping up a customer's Alipay / WeChat
 * wallet, paying a China merchant for them (proxy pay), or transferring to a China bank. Kept
 * apart from the RM shipping wallet; nothing here touches its balance.
 */

/**
 * One exchange-rate tier from `currencyRate` (MYP-59).
 *
 * The tiers go by the **RM** amount, not the RMB one: live quotes (2026-10-02) switch rate when
 * the RM total crosses `rmltamt1` / `rmltamt2` — ¥4,775 (≈ RM 3,001) still quoted 1.591 while
 * ¥4,790 quoted 1.596. The Jira card's "< ¥3,000" was a misreading.
 */
export interface RateTier {
  /** Where the tier starts, in RM cents. 0 for the first tier. */
  readonly fromRmCents: number;
  /** Where the next tier starts, in RM cents. Absent for the last, open-ended tier. */
  readonly toRmCents?: number;
  /**
   * RMB per RM 1, exactly as the vendor sends it (`"1.591"`): RM = ¥ ÷ rate. Kept as text so it
   * is shown digit for digit, never rounded through a float.
   */
  readonly rate: string;
}

/**
 * A "percentage, with a flat fee for small amounts" rule — how the old website prices top-ups
 * (3%, or RM 10 under ¥500) and proxy pay (5%, or RM 20 under ¥500).
 */
export interface PercentFeeRule {
  /** `3` for 3%. */
  readonly percent: number;
  /** Below this RMB amount (in fen) the flat fee applies instead of the percentage. */
  readonly flatBelowRmbCents: number;
  /** The flat fee, in cents of `flatFeeCurrency`. */
  readonly flatFeeCents: number;
  /** As the vendor spells it: `"RM"`. */
  readonly flatFeeCurrency: string;
}

/** The fee settings `currencyRate` carries, as the old website shows them. */
export interface ChinaPaymentFees {
  /** `alipayfee` / `alipayleast` / `servicesfee`: Alipay and WeChat top-up, and Alipay transfer. */
  readonly topUp?: PercentFeeRule;
  /** `alipayfee2` / `alipayleast2` / `servicesfee2`: proxy pay. */
  readonly proxyPay?: PercentFeeRule;
  /** `transfee`: charged per bank transfer to China, in RMB fen. */
  readonly bankTransferRmbCents?: number;
}

/** The rate card (MYP-59): `currencyRate`'s table for RMB. */
export interface RateCard {
  /** In order, lowest RM amount first; neighbouring tiers with the same rate are merged. */
  readonly tiers: readonly RateTier[];
  readonly fees: ChinaPaymentFees;
  /** `status: "Y"`. Anything else means the service is switched off on the vendor's side. */
  readonly active: boolean;
}

/**
 * The request types the API knows (`alipay_rechange`, `ChargeResult`): Alipay top-up, WeChat
 * top-up, proxy pay (Alipay) and bank transfer to China. The old website's Alipay transfer and
 * WeChat proxy pay have no type (China Payment API issues, item 3), so the app does not offer them.
 */
export type ChinaPaymentType = 'ALIPAYCHARGE' | 'WXCHARGE' | 'ALIPAY' | 'BANKTRANS';

/**
 * A live quote from `alipay_rechange` (MYP-60), for an RMB amount. Every figure is the server's,
 * rounded to cents for display and nothing else — no fee is worked out in the browser (MYP-64).
 */
export interface Quote {
  readonly type: ChinaPaymentType;
  /** The amount asked about, in fen. */
  readonly rmbCents: number;
  /** ¥ per RM 1, as sent (`"1.591"`). */
  readonly rate: string;
  /** In cents of `serviceFeeCurrency`. 0 when the server charges none. */
  readonly serviceFeeCents: number;
  /** RM for top-ups and proxy pay; RMB for bank transfer (its ¥15 per transfer). */
  readonly serviceFeeCurrency: 'RM' | 'RMB';
  /** `sumpays`: what the customer pays, in RM cents. */
  readonly totalRmCents: number;
  /** `rmpay`, the amount in RM before fees — only bank transfer sends it. */
  readonly rmAmountCents?: number;
}

/**
 * One MyPoz account the customer pays into (MYP-61), from `getBankList` with `accounttype=ALIPAY`:
 * live (2026-10-02) Public Bank, Maybank, DuitNow and Touch 'n Go, all RM.
 */
export interface PayToAccount {
  /** The vendor's id — what `ChargeResult` will take as `bankid` (MYP-62). */
  readonly id: string;
  /** For display: "Public Bank", not the vendor's "PUBLIC BANK - TOP UP FOR ALIPAY". */
  readonly name: string;
  /**
   * The account number, when there is one to copy (the banks). The e-wallets send the company's
   * name in that field instead, and are paid by QR.
   */
  readonly accountNumber?: string;
  /** Who the account belongs to (`accname`), e.g. "MYPOST ONLINE SDN BHD". */
  readonly accountName?: string;
  /** The QR picture (`accimg`), a site path. DuitNow and Touch 'n Go share one DuitNow QR poster. */
  readonly qrPath?: string;
  /** The logo (`imgurl`), a site path. */
  readonly logoPath?: string;
}

/**
 * A China Payment request being filled in (MYP-62, MYP-109), for `ChargeResult`. Text fields hold
 * what was typed; nothing is trimmed until the request is built.
 *
 * There is deliberately **no password field**. The old website asks for the Alipay / WeChat login
 * password (top-ups) and the Taobao password (proxy pay), and `ChargeResult` takes them as `tbpw`;
 * the app never asks for one, so none can ever be sent, logged or kept (MYP-109).
 *
 * There is no payment time either: the old website doesn't ask, and `date` is the moment the
 * request is sent (`toChargeResultParams`).
 */
export interface ChargeDraft {
  readonly type: ChinaPaymentType;
  /** The amount, in fen — from the amount box (`parseRmbAmount`). */
  readonly rmbCents?: number;
  /** The quote's total for that amount, in RM cents — for the RM 100 minimum. */
  readonly totalRmCents?: number;
  /** The MyPoz account paid into (`getBankList` id) — sent as `bankid`. */
  readonly bankId?: string;
  /** The receipt's stored path, from `uploadImg`. */
  readonly receiptPath?: string;
  readonly name: string;
  readonly phone: string;
  readonly idNumber: string;
  /**
   * The customer's own account the request is for: the Alipay account to top up, the WeChat ID to
   * top up, or the Taobao account for proxy pay. Sent as `tbac` — the document's "Taobao / Alipay
   * account". Not used for bank transfer.
   */
  readonly payerAccount: string;
  /** Proxy pay only: the seller being paid (`sellername`). Optional. */
  readonly sellerName: string;
  /** Bank transfer only: who receives the money in China, and where. */
  readonly recipientName: string;
  readonly recipientCardNumber: string;
  readonly recipientBank: string;
  readonly recipientBranch: string;
  readonly remark: string;
}

/**
 * What the app allows for one request type (MYP-109). `maxRmbCents`: the largest amount it lets a
 * customer send — set where the vendor's quote is known to be wrong above it (api-issue #27).
 */
export interface ChargeLimits {
  readonly maxRmbCents?: number;
}

/** The old website's minimum per request: RM 100, on the total to pay. */
export const CHINA_PAYMENT_MIN_TOTAL_RM_CENTS = 10_000;

/** The three steps of a request, as on the old website (MYP-109). */
export type ChargeStep = 'amount' | 'details' | 'payment';

/** What still stops a draft from being sent, grouped by the step that asks for it. */
export type ChargeDraftProblem =
  // Step 1, amount.
  | 'amount'
  | 'overLimit'
  | 'quote'
  | 'minimum'
  // Step 2, your details.
  | 'name'
  | 'idNumber'
  | 'phone'
  | 'payerAccount'
  | 'recipientName'
  | 'recipientCardNumber'
  | 'recipientBank'
  // Step 3, pay and upload.
  | 'bank'
  | 'receipt';
